import {EditPermissions} from '../../../../../../../shared/models';
import {getEditTypes} from '../../../delegatorUtils';
import {required} from '../../../../../../../shared/utils';
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn} from '@angular/forms';
import {newStepValue} from '../../../../../services/flowBuilder/utils/constants';
import { noSpaces } from "../../../../../../../shared/utils/validators.utils";

interface ActionParams {
    name: string;
    description: string;
    reference: string;
    report?: string;
}

interface ActionResult {
    key: string;
    next: string;
}

export interface WaitForCallbackFormType {
    actionParams: UntypedFormGroup;
    actionResults: UntypedFormArray;
    actionDefaultResults: UntypedFormArray;
}

const DEFAULT_RESULTS = ['other', 'timeout'];

export class WaitForCallback {

    action = {};

    constructor(nodeType: string) {
        this.action = {
            actionType: nodeType,
        };
    }

    addActionParams(actionParams: ActionParams) {
        this.action = {
            ...this.action,
            name: actionParams.name,
            description: actionParams.description,
            report: actionParams.report,
            actionParams: {
                reference: actionParams.reference.trim()
            }
        };
    }

    addResults(actionResult: ActionResult[]) {
        let result = actionResult.reduce((acc, r) => ({...acc, [r.key]: r.next}), {});

        if (this.action['result']) {
            result = {...result, ...this.action['result']};
        }


        this.action = {
            ...this.action,
            result
        };
    }

    get nodeData(): {} {
        return {
            action: this.action,
            type: 'action',
            metadata: {
                nodeVersion: '1.0.0'
            }
        };
    }

    static toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): WaitForCallbackFormType {
        return {
            actionParams: WaitForCallbackForm.actionParamsForm(getActionParams(stepData), editPermissions),
            actionResults: WaitForCallbackForm.actionResultForm(getActionResult(stepData), editPermissions),
            actionDefaultResults: WaitForCallbackForm.actionDefaultResultForm(getActionResult(stepData))
        };
    }
}

const getActionParams = (stepData: { [index: string]: any }): ActionParams => {
    const { action } = stepData;
    return {
        name: action?.name ? action.name : '',
        description: action?.description ? action.description : '',
        reference: action?.actionParams.reference ? action.actionParams.reference : '',
        report: action?.report ? action.report : ''
    };
};

const getActionResult = (stepData: { [index: string]: any }): ActionResult[] => {
    const results: { [index: string]: any } = stepData.action?.result ? stepData.action.result : getDefaultActionResults();
    const newResult: ActionResult[] = [];
    for (const result in results) {
        newResult.push({
            key: result,
            next: results[result]
        });
    }
    return newResult;
};

const getDefaultActionResults = () => {
    return {
        other: newStepValue,
        timeout: newStepValue,
    };
};

export class WaitForCallbackForm {

    static actionParamsForm(actionParams: ActionParams, editPermissions: EditPermissions): UntypedFormGroup {
        const {editFlows} = getEditTypes(editPermissions);
        return new UntypedFormGroup({
            name: new UntypedFormControl({
                value: actionParams.name,
                disabled: !editFlows
            }, [required('Name'), noSpaces("Name can't contain spaces")]),
            description: new UntypedFormControl({
                value: actionParams.description,
                disabled: !editFlows
            }, required('Description')),
            reference: new UntypedFormControl({
                value: actionParams.reference,
                disabled: !editFlows
            }, [required('Reference'), requireVariable()]),
            report: new UntypedFormControl({
                value: actionParams.report,
                disabled: !editFlows
            })
        });
    }

    static actionResultForm(actionResult: ActionResult[], editPermissions: EditPermissions): UntypedFormArray {
        const actionResultFiltered = actionResult.filter(r => !DEFAULT_RESULTS.includes(r.key));
        return this.getActionResults(actionResultFiltered, editPermissions);
    }

    static actionDefaultResultForm(actionResult: ActionResult[]): UntypedFormArray {
        const actionResultFiltered = actionResult.filter(r => DEFAULT_RESULTS.includes(r.key));
        return this.getActionResults(actionResultFiltered, {canEdit: false, canEditFlows: false, canEditMenuTextOnly: false});
    }

    private static getActionResults(actionResultFiltered: ActionResult[], editPermissions: EditPermissions) {
        const formArray = new UntypedFormArray([]);
        for (const actionResultKey of actionResultFiltered) {
            formArray.push(this.resultTemplate(editPermissions));
        }
        formArray.patchValue(actionResultFiltered);
        return formArray;
    }

    static resultTemplate(editPermissions: EditPermissions): UntypedFormGroup {
        const {editFlows} = getEditTypes(editPermissions);
        return new UntypedFormGroup({
            key: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }, [required('Field')]),
            next: new UntypedFormControl(newStepValue),
        });
    }
}
const requireVariable = (message?: string): ValidatorFn => {
    const VARIABLE_REGEXP = /^.*\[\[[\w\s\W]+\]\].*$/;
    if (!message) {
        message = 'Requires a variable';
    }
    return (control: AbstractControl): { [key: string]: any } | null => {
        const value: string = control.value;
        const forbidden = !VARIABLE_REGEXP.test(value);
        return forbidden ? { requireVariable: message } : null;
    };
}
